import { MessageSquareText, LogIn, UploadCloud } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/components/theme/theme-provider';
import Logo from '@/assets/images/tulasi-circle.png';
import LogoWhite from '@/assets/images/tulasi-circle_white.png';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { analytics } from '@/services/analytics';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { sendMessage } from '@/features/chat/chatSlice';
import { selectIsAuthenticated } from '@/features/auth/authSlice';
import { selectHasMeaningfulOrder } from '@/features/order/orderSlice';
import { supervisorService } from '@/services/supervisorService';
import { setUiPayload } from '@/features/canvas/canvasSlice';

const STUDIO_OPEN_CHAT_EVENT = 'studio:open-chat';

const hasLoggedInBefore = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem('tulasi.hasLoggedInBefore') === 'true';
  } catch {
    return false;
  }
};

const DefaultCanvas = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const hasOrderContent = useAppSelector(selectHasMeaningfulOrder);
  const seenAuthBefore = useMemo(() => hasLoggedInBefore(), []);

  const promptCopy = useMemo(
    () =>
      t(
        'canvas.placeholder.promptCopy',
        'We keep things effortless. Tell our AI concierge what you need, just like chatting with a consultant.',
      ),
    [t],
  );

  const handleChatPrompt = useCallback(() => {
    const suggested = t(
      'canvas.placeholder.suggestedPrompt',
      'What kinds of services can you help me with?',
    );
    analytics.track('DefaultCanvas: ui.canvas_card_click', {
      card: 'chatPrompt',
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(STUDIO_OPEN_CHAT_EVENT));
    }
    void dispatch(sendMessage(suggested));
  }, [dispatch, t]);

  const handleAuthClick = useCallback(() => {
    analytics.track('DefaultCanvas: ui.canvas_card_click', {
      card: isAuthenticated ? 'account' : seenAuthBefore ? 'login' : 'register',
    });
    if (!isAuthenticated) {
      dispatch(setUiPayload({ authMode: seenAuthBefore ? 'login' : 'register' }));
      supervisorService.send({ type: 'ORDER.APPLY_PATCH', patch: {} });
      supervisorService.send({ type: 'UI.SHOW_LOGIN' });
    }
  }, [dispatch, isAuthenticated, seenAuthBefore]);

  const handleUploadClick = useCallback(() => {
    analytics.track('DefaultCanvas: ui.canvas_card_click', {
      card: 'upload',
    });
    if (!isAuthenticated) {
      dispatch(setUiPayload({ authMode: seenAuthBefore ? 'login' : 'register' }));
      supervisorService.send({ type: 'ORDER.APPLY_PATCH', patch: {} });
      supervisorService.send({ type: 'UI.SHOW_LOGIN' });
      return;
    }

    supervisorService.send({ type: 'ORDER.APPLY_PATCH', patch: {} });
    supervisorService.send({ type: 'UI.GOTO_STEP', step: 'upload' });
  }, [dispatch, isAuthenticated, seenAuthBefore]);

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-8 px-6 py-10 text-muted-foreground">
      <div className="flex flex-col items-center text-center gap-4 max-w-xl">
        <img
          src={theme === 'dark' ? LogoWhite : Logo}
          alt="Tulasi Home Logo"
          className="mx-auto h-20 w-20 object-contain drop-shadow-lg"
        />
        <p className="text-base font-medium leading-relaxed text-foreground/90">
          {promptCopy}
        </p>
      </div>

      <div className="grid w-full max-w-4xl gap-4 md:grid-cols-3">
        <Card className="flex flex-col justify-between border-primary/20 bg-primary/5 backdrop-blur">
          <CardHeader className="flex flex-row gap-3 items-start">
            <div className="rounded-full bg-primary/10 p-2 text-primary">
              <MessageSquareText className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-primary">
                {t('canvas.placeholder.cards.chatTitle', 'Ask the AI concierge')}
              </CardTitle>
              <CardDescription className="text-sm text-primary/80">
                {t(
                  'canvas.placeholder.cards.chatDescription',
                  'Start the conversation with a ready-made question and keep the ideas flowing.',
                )}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              className="w-full whitespace-normal h-auto min-h-[3.25rem] py-3 leading-snug"
              onClick={handleChatPrompt}
            >
              {t('canvas.placeholder.cards.chatCta', 'Ask “What services do you offer?”')}
            </Button>
          </CardContent>
        </Card>

        {!isAuthenticated && (
          <Card className="flex flex-col justify-between border-border/60 bg-muted/70 backdrop-blur">
            <CardHeader className="flex flex-row gap-3 items-start">
              <div className="rounded-full bg-foreground/10 p-2 text-foreground">
                <LogIn className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-foreground">
                  {seenAuthBefore
                    ? t('canvas.placeholder.cards.loginTitle', 'Welcome back—sign in to continue')
                    : t('canvas.placeholder.cards.registerTitle', 'Create your Tulasi account')}
                </CardTitle>
                <CardDescription className="text-sm text-foreground/70">
                  {seenAuthBefore
                    ? t('canvas.placeholder.cards.loginDescription', 'Pick up where you left off and access your saved designs.')
                    : t('canvas.placeholder.cards.registerDescription', 'Save your prints, track progress, and unlock personalized help.')}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                className="w-full whitespace-normal h-auto min-h-[3.25rem] py-3 leading-snug"
                onClick={handleAuthClick}
              >
                {seenAuthBefore
                  ? t('canvas.placeholder.cards.loginCta', 'Open the login form')
                  : t('canvas.placeholder.cards.registerCta', 'Start the registration form')}
              </Button>
            </CardContent>
          </Card>
        )}

        {!hasOrderContent && (
          <Card className="flex flex-col justify-between border-emerald-300/60 bg-emerald-50 dark:bg-emerald-950/30">
            <CardHeader className="flex flex-row gap-3 items-start">
              <div className="rounded-full bg-emerald-500/10 p-2 text-emerald-500">
                <UploadCloud className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-emerald-600 dark:text-emerald-300">
                  {t('canvas.placeholder.cards.uploadTitle', 'Upload artwork for printing')}
                </CardTitle>
                <CardDescription className="text-sm text-emerald-600/80 dark:text-emerald-200/80">
                  {t(
                    'canvas.placeholder.cards.uploadDescription',
                    'Share an image or design so we can craft a custom print for you.',
                  )}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                className="w-full whitespace-normal h-auto min-h-[3.25rem] py-3 leading-snug bg-emerald-500 hover:bg-emerald-500/90 text-white"
                onClick={handleUploadClick}
              >
                {t('canvas.placeholder.cards.uploadCta', 'Upload an image')}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DefaultCanvas;
